/* eslint-disable complexity */
import { Navigate, useRoutes } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { DashboardLayout } from 'sections/layout';
import { PageNotFound } from 'pages/404App';
import { HomeApp } from 'pages/HomeApp';
import { pagesEnum } from 'constants/ui.constants';
import { Login } from 'pages/Login';
import { ContentApp } from 'pages/ContentApp';
import { Authorization } from 'pages/Authorization';
import { BusinessModelApp } from 'pages/BusinessModelApp';
import { PendingRequestsApp } from 'pages/PendingRequestsApp';
import { HyperMonitoringApp } from 'pages/HyperMonitoringApp';
import { Reports } from 'components/Reports';
import { NewAccountsApp } from 'pages/AccountsApp';
import { DashboardApp } from 'pages/DashboardApp';
import { AccountsInProcess } from 'pages/AccountsInProcess';
import { CompanyFeatureApproveApp } from 'pages/CompanyFeatureApproveApp';
import { CompanyFeatureProfileApp } from 'pages/CompanyFeatureProfileApp';
import { CompanyFeatureCreateApp } from 'pages/CompanyFeatureCreateApp';
import { CustomerAccountsApp } from 'pages/CustomerAccountsApp';
import { BoSignerDashboard } from 'pages/BoSignerDashboard';
import { CustomerAccountsProfileApp } from 'pages/CustomerAccountsProfileApp';
import { customerInitialAction } from 'constants/customer-account.constants';
import { WithdrawalApp } from 'pages/WithdrawalApp';
import { CountriesManagementApp } from 'pages/CountriesManagementApp';
import { DigitalDepositBankAvailabilityApp } from 'pages/DigitalDepositBankAvailabilityApp';
import { CustomerRelationsManagementApp } from 'pages/CustomerRelationsManagementApp';
import { PaymentsPurposeManagementApp } from 'pages/PaymentsPurposeManagementApp';
import { UpsertPaymentsPurpose } from 'sections/PaymentsPurposeManagement/UpsertPaymentsPurpose';
import { PaymentCategoriesApp } from 'pages/PaymentCategoriesApp';
import { UpsertPaymentCategory } from 'sections/PaymentCategoriesManagement/components/UpsertPaymentCategory';
import { MainDashboard } from 'pages/MainDashboard';
import { MainDashboardByCategory } from 'pages/MainDashboardByCategory';
import { BeneficiaryDashboardPage } from 'pages/BeneficiaryDashboardPage';
import { CustomerBenefitsManagement } from 'pages/CustomerBenefitsManagement';
import SystemSettings from 'pages/SystemSettings';
import { DigitalBalanceRequestsApp } from 'pages/DigitalBalanceRequestsApp';
import { WebhookOperationsDashboardApp } from 'pages/WebhookOperationsDashboardApp';
import { WebhookOperationsTableApp } from 'pages/WebhookOperationsTableApp';
import { SystemMessagesApp } from 'pages/SystemMessagesApp';
import { CreateSystemMessagePage } from 'pages/CreateSystemMessagePage';

interface IProps {
	token: string;
}

export const isAuthenticated = (token: string): boolean => {
	if (!token) {
		return false;
	}

	try {
		const decodedToken: any = jwtDecode(token);
		if (!decodedToken) {
			return false;
		}
		if (decodedToken.exp) {
			const currentTime = Date.now() / 1000;
			return decodedToken.exp > currentTime;
		}
	} catch (error) {
		return false;
	}

	return true;
};

export const Router = ({ token }: IProps) => {
	return useRoutes([
		{
			path: '/',
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: '', element: <HomeApp /> }],
		},
		{
			path: '/accounts',
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [
				{ path: '', element: <AccountsInProcess /> },
				{ path: pagesEnum.NEW_ACCOUNT + '/:action', element: <NewAccountsApp /> },
				{ path: pagesEnum.NEW_ACCOUNT + '/:action/:id', element: <NewAccountsApp /> },
			],
		},
		{
			path: '/' + pagesEnum.DASHBOARD,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [
				{ path: '', element: <DashboardApp /> },
				{
					path: `/${pagesEnum.DASHBOARD}/${pagesEnum.CLIENTS_DASHBOARD_SIGNER}`,
					element: <BoSignerDashboard />,
				},
			],
		},
		{
			path: '/' + pagesEnum.MAIN_DASHBOARD,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [
				{ path: '', element: <MainDashboard /> },
				{ path: 'beneficiaries', element: <BeneficiaryDashboardPage /> },
				{ path: ':category', element: <MainDashboardByCategory /> },
				{ path: ':category/:plan', element: <MainDashboardByCategory /> },
			],
		},
		{
			path: '/' + pagesEnum.CONTENT_PAGE,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: '', element: <ContentApp /> }],
		},
		{
			path: '/' + pagesEnum.PLANS_BUSINESS_MODEL,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: '', element: <BusinessModelApp /> }],
		},
		{
			path: '/' + pagesEnum.BENEFITS,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: '', element: <CustomerBenefitsManagement /> }],
		},
		{
			path: '/' + pagesEnum.REPORTS,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: '', element: <Reports /> }],
		},
		{
			path: '/' + pagesEnum.PENDING_REQUESTS,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [
				{ path: '', element: <PendingRequestsApp /> },
				{ path: ':type', element: <PendingRequestsApp /> },
				//can put specific type or "all" for all types
				{ path: ':type/:companyId', element: <PendingRequestsApp /> },
			],
		},
		{
			path: '/' + pagesEnum.HYPER_MONITORING,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [
				{ path: '', element: <HyperMonitoringApp /> },
				{ path: ':type', element: <HyperMonitoringApp /> },
			],
		},
		{
			path: '/' + pagesEnum.DIGITAL_BALANCE_REQUESTS,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: '', element: <DigitalBalanceRequestsApp /> }],
		},
		{
			path: '/' + pagesEnum.COMPANY_FEATURE_APPROVE,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: ':type/:id', element: <CompanyFeatureApproveApp /> }],
		},
		{
			path: '/' + pagesEnum.COMPANY_FEATURE_CREATE,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [
				{ path: 'system-messages', element: <CreateSystemMessagePage mode="company" /> },
				{ path: ':type', element: <CompanyFeatureCreateApp /> },
			],
		},
		{
			path: '/' + pagesEnum.COMPANY_FEATURE_PROFILE,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: ':type/:id', element: <CompanyFeatureProfileApp /> }],
		},
		{
			path: '/' + pagesEnum.COMPANY_FEATURE_PROFILE,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: ':type/:id/:subType', element: <CompanyFeatureProfileApp /> }],
		},
		{
			path: '/' + pagesEnum.CUSTOMER_ACCOUNTS,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [
				{ path: '', element: <CustomerAccountsApp /> },
				{ path: ':id', element: <CustomerAccountsApp /> },
				{
					path: ':id/:initialAction',
					element: <CustomerAccountsApp />,
				},
				{
					path: ':id/' + customerInitialAction.WITHDRAWL_ACCOUNT + '/request/:requestId',
					element: <WithdrawalApp />,
				},
			],
		},
		{
			path: '/' + pagesEnum.CUSTOMER_ACCOUNTS_DETAILS,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [
				{ path: ':companyId/:type', element: <CustomerAccountsProfileApp /> },
				{ path: ':companyId/:type/:dataId', element: <CustomerAccountsProfileApp /> },
			],
		},
		{
			path: '/' + pagesEnum.COUNTRIES_MANAGEMENT,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: '', element: <CountriesManagementApp /> }],
		},
		{
			path: '/' + pagesEnum.DIGITAL_DEPOSIT_BANK_AVAILABILITY,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: '', element: <DigitalDepositBankAvailabilityApp /> }],
		},
		{
			path: '/' + pagesEnum.PAYMENTS_PURPOSE_MANAGEMENT,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [
				{ path: '', element: <PaymentsPurposeManagementApp /> },
				{ path: ':id', element: <UpsertPaymentsPurpose /> },
			],
		},
		{
			path: '/' + pagesEnum.PAYMENT_CATEGORIES,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [
				{ path: '', element: <PaymentCategoriesApp /> },
				{ path: ':id', element: <UpsertPaymentCategory /> },
			],
		},
		{
			path: '/' + pagesEnum.MANAGE_CUSTOMER_RELATIONS,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: '', element: <CustomerRelationsManagementApp /> }],
		},
		{
			path: '/' + pagesEnum.SYSTEM_SETTINGS,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: '', element: <SystemSettings /> }],
		},
		{
			path: '/' + pagesEnum.SYSTEM_MESSAGES,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: '', element: <SystemMessagesApp /> }],
		},
		{
			path: '/' + pagesEnum.SYSTEM_MESSAGES_CREATE,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: '', element: <CreateSystemMessagePage mode="broadcast" /> }],
		},
		{
			path: '/' + pagesEnum.WEBHOOK_MONITORING,
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [
				{ path: pagesEnum.WEBHOOK_OPERATIONS_DASHBOARD, element: <WebhookOperationsDashboardApp /> },
				{ path: 'webhook-operations/:status', element: <WebhookOperationsTableApp /> },
			],
		},
		{
			path: '/' + pagesEnum.LOGIN,
			element: isAuthenticated(token) ? (
				<Navigate to={`/${pagesEnum.DASHBOARD}`} replace />
			) : (
				<DashboardLayout nosidebar={true} />
			),
			children: [{ path: '', element: <Login /> }],
		},
		{
			path: '/' + pagesEnum.AUTHORIZATION,
			element: <Authorization></Authorization>,
		},
		{
			path: '*',
			element: <Navigate to="/404" replace />,
		},
		{
			path: '/404',
			element: isAuthenticated(token) ? <DashboardLayout /> : <Navigate to="/login" replace />,
			children: [{ path: '', element: <PageNotFound /> }],
		},
	]);
};
